/**
 * useCapability: read-only check against the active ecosystem's per-tenant
 * feature flags (`Ecosystem.capabilities`).
 *
 * The eight canonical flags (see `server/src/api/ecosystem/ecosystem.model.js`)
 * all default to `true` on the schema. This hook treats every unspecified
 * state as "enabled" so that:
 *
 *   - A tenant whose ecosystem record predates a newly-added flag is not
 *     silently locked out of the new feature.
 *   - Platform-mode surfaces (the apex, where `ecosystem` is `null`) are
 *     never gated. Capability flags are a per-tenant concept; the platform
 *     apex has its own platform-specific UX layer.
 *   - A briefly-loading ecosystem context does not flicker features off and
 *     then on as `ecosystem` resolves.
 *
 * The negative-space gate ("return `false` ONLY when the flag is explicitly
 * `false`") is the contract: it inverts the default-allow expectation only
 * when the admin has deliberately turned a feature off.
 *
 * Mirror this on the server with `requireCapability(name)` middleware: the
 * client check is a UX affordance; the server is the boundary.
 *
 * @param {string} name - One of the canonical capability flag names. See
 *   `Ecosystem.capabilities` for the authoritative list (`mentoring`,
 *   `opportunities`, `opportunityPartnerships`, `networkGraph`,
 *   `affiliations`, `discoverPartners`, `ecosystemAnalytics`, `aiAssistant`).
 * @returns {boolean} `true` unless the active tenant has explicitly disabled
 *   the flag (`capabilities[name] === false`).
 */
import { useEcosystem } from '@/shared/contexts/EcosystemContext';

export default function useCapability(name) {
    const { isPlatform, capabilities } = useEcosystem();

    // Platform apex is never capability-gated: those routes are platform-
    // level and have their own gating logic (PlatformAdminRoute, etc.).
    if (isPlatform) return true;

    // Defensive: a null `capabilities` (ecosystem loaded without the field,
    // or context still settling) defaults to enabled. Only an explicit
    // `false` disables: matches the schema's default-true semantics.
    if (!capabilities) return true;

    return capabilities[name] !== false;
}
